// src/components/buyer-leads/main-buyer-leads.jsx
// Buyers pipeline — Board | List toggle page shared with the Listing pipeline
// via PipelineViewSwitch (board of stage columns, or a sortable list).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import PageHeader from "../PageHeader";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  listenBuyerLeads,
  updateBuyerLeadStage,
  updateBuyerLead,
  deleteBuyerLead,
  getDeletedBuyerLeads,
  restoreBuyerLead,
  permanentlyDeleteBuyerLead,
} from "../../utils/firebasefunctions";
import {
  BUYER_STAGE_ORDER,
  getBuyerStageColor,
  normalizeStage,
  BUYER_PARKING_STAGES,
} from "../../utils/buyerLeadStages";
import { leadSourceLabel } from "../../utils/leadSourceLabel";
import { excludeTestAgents } from "../../utils/testAgents";
import { clientNames } from "../../utils/leadClients";
import { isOnLead } from "../../utils/leadOwnership";
import { selectVisibleDeletedLeads } from "../../utils/deletedLeads";
import NewBuyerLeadModal from "./NewBuyerLeadModal";
import BuyerLeadDrawer from "./BuyerLeadDrawer";
import PipelineViewSwitch from "../pipeline/PipelineViewSwitch";
import { isoDaysFromNow } from "../../utils/pipelineView";
import { usePersistentAgentFilter } from "../../hooks/usePersistentAgentFilter";
import { useActionDialog } from "../ActionDialog";

export default function MainBuyerLeads() {
  const [{ user, buyerLeads, employees, leadsource }, dispatch] = useStateValue();
  const { confirm, prompt, dialog } = useActionDialog();
  const [showLost, setShowLost] = useState(false);
  const [showWon, setShowWon] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // "Viewing: <agent>" filter — shared hook: defaults to My Leads, persists per
  // user across visits. Switch to "All Agents" or another agent to look around.
  const [viewAgent, setViewAgent] = usePersistentAgentFilter(user?.id, "buyer");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const unsub = listenBuyerLeads((rows) => {
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows });
    });
    return () => unsub && unsub();
  }, [dispatch]);

  // Normalize legacy stage names on read so renamed stages still group
  // correctly (Contacted → Nurturing, Offer Out → Offer Submitted).
  const leads = useMemo(
    () => (buyerLeads || []).map((l) => ({ ...l, stage: normalizeStage(l.stage) })),
    [buyerLeads],
  );

  // Deep link: /dashboard/buyers?lead=<id> opens that lead's drawer once
  // the list is in. Runs on the FULL list so lost/converted leads open too.
  const location = useLocation();
  // Remember WHICH lead was opened, not merely that one was. A plain boolean
  // made this one-shot for the life of the page: the header search sends you
  // here repeatedly with a different ?lead=, React Router updates the location
  // without remounting, and every result after the first was a dead click.
  const [deepLinkedId, setDeepLinkedId] = useState(null);
  useEffect(() => {
    const target = new URLSearchParams(location.search).get("lead");
    if (!target || target === deepLinkedId || !leads.length) return;
    if (leads.find((l) => l.id === target)) {
      setSelectedId(target);
      setDeepLinkedId(target);
    }
  }, [location.search, leads, deepLinkedId]);

  const isPrivileged =
    user?.role === "Admin" || user?.role === "Supervisor" || user?.role === "Accountant";

  // Deleted leads can never arrive through listenBuyerLeads — it strips
  // deletedAt > 0 before rows reach app state. Fetch them separately. The PAGE
  // does this rather than the tab, because the tab's count must exist before
  // anyone opens it.
  const [deletedRows, setDeletedRows] = useState([]);
  const loadDeleted = useCallback(async () => {
    try {
      setDeletedRows((await getDeletedBuyerLeads()) || []);
    } catch (err) {
      console.error("Could not load deleted buyer leads:", err);
    }
  }, []);
  useEffect(() => {
    loadDeleted();
  }, [loadDeleted]);

  const visibleDeleted = useMemo(
    () => selectVisibleDeletedLeads({ rows: deletedRows, user, viewAgent, isPrivileged }),
    [deletedRows, user, viewAgent, isPrivileged],
  );

  const restoreDeleted = async (lead) => {
    try {
      await restoreBuyerLead(lead.id);
      toast.success("Lead restored");
      loadDeleted();
    } catch (err) {
      console.error("Restore failed:", err);
      toast.error("Could not restore lead");
    }
  };

  const purgeDeleted = async (lead) => {
    const ok = await confirm({
      title: "Delete Permanently",
      body: `Permanently delete "${lead.contactName || lead.propertyAddress || "this lead"}"?`,
      note: "This cannot be undone.",
      tone: "danger",
      confirmLabel: "Delete forever",
    });
    if (!ok) return;
    try {
      await permanentlyDeleteBuyerLead(lead.id);
      toast.success("Deleted forever");
      loadDeleted();
    } catch (err) {
      console.error("Permanent delete failed:", err);
      toast.error("Could not delete lead");
    }
  };

  // Agents for the "Viewing" dropdown, with the current user guaranteed to be
  // present (and listed first) so "default to me" always has a matching option.
  const agentOptions = useMemo(() => {
    const list = excludeTestAgents(employees || []);
    if (user?.id && !list.some((e) => e.id === user.id)) {
      return [
        { id: user.id, firstname: user.firstname, lastname: user.lastname, email: user.email },
        ...list,
      ];
    }
    return list;
  }, [employees, user]);


  const canManage = (lead) =>
    user?.role === "Admin" ||
    user?.role === "Supervisor" ||
    isOnLead(lead, user?.id, { ownerlessFallback: true });

  // Board-only search across the fields shown on a card: name, areas they're
  // shopping, price, property type, timeline, and the assigned agent.
  const searchTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchesQuery = (l) => {
    if (searchTokens.length === 0) return true;
    const haystack = [
      clientNames(l),
      l.contactName,
      ...(Array.isArray(l.areas) ? l.areas : []),
      ...(Array.isArray(l.propertyTypes) ? l.propertyTypes : []),
      l.timeline,
      l.assignedAgentName,
      l.priceMin,
      l.priceMax,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchTokens.every((t) => haystack.includes(t));
  };

  const visibleLeads = useMemo(
    () =>
      leads.filter((l) => {
        // Won/converted buyers leave the active pipeline; "Show Won" brings
        // them back as a fully-converted tracker (dimmed, WON ✓ badge).
        if (l.status === "converted" && !showWon) return false;
        if (!showLost && l.status === "lost") return false;
        if (!matchesQuery(l)) return false;
        if (isPrivileged) {
          if (viewAgent === "all") return true;
          return isOnLead(l, viewAgent);
        }
        // Agents' own board: ownerless leads they entered stay visible to
        // them (they have no "All Agents" view to find them in).
        return isOnLead(l, user?.id, { ownerlessFallback: true });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leads, showLost, showWon, isPrivileged, user?.id, viewAgent, query],
  );

  // No won-count badge on this toolbar. It counted EVERY converted buyer on the
  // team with no agent filter, so an agent read the team's tally as if it were
  // their own (Henry, 2026-08-12: "it doesnt make sense for everyone to see
  // that when it is not their wins"). The "Show Won" checkbox still reveals the
  // won cards, and those ARE scoped by the agent filter below.

  // Stage moves happen via the row dropdown (and the drawer).
  const moveStage = async (lead, newStage) => {
    if (!lead || !newStage || newStage === lead.stage) return;
    if (!canManage(lead)) {
      toast.error("You can only move your own leads");
      return;
    }
    dispatch({
      type: actionType.STORE_BUYER_LEADS,
      payload: leads.map((l) => (l.id === lead.id ? { ...l, stage: newStage } : l)),
    });
    const res = await updateBuyerLeadStage(lead.id, newStage);
    if (!res.success) toast.error("Failed to move lead");
  };

  const selectedLead = leads.find((l) => l.id === selectedId) || null;

  // Middle column of a row: what they're shopping for, at a glance.
  const fmtK = (v) => {
    const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
    return n > 0 ? `$${n.toLocaleString("en-US")}` : "";
  };
  // Shaped to match the listing card, which reads better: ONE prominent line
  // carrying identity + money, then a single muted line. For a listing that's
  // "address · list price" then the source; a buyer has no address, so their
  // identity is what they're shopping for.
  const secondaryLine = (l) => {
    const price =
      fmtK(l.priceMin) && fmtK(l.priceMax)
        ? `${fmtK(l.priceMin)}–${fmtK(l.priceMax)}`
        : fmtK(l.priceMax) || fmtK(l.priceMin);
    const types = Array.isArray(l.propertyTypes)
      ? l.propertyTypes.filter(Boolean).slice(0, 2).join(" · ")
      : "";
    const areas =
      Array.isArray(l.areas) && l.areas.length ? l.areas.slice(0, 2).join(", ") : "";
    return [price, types, areas].filter(Boolean).join(" · ");
  };

  // One muted line, same as the listing card: where the lead came from. The
  // beds/baths/sqft/timeline detail that used to stack up here lives in the
  // drawer — the listing card sends you there for its extra detail too.
  // Pre-approval only earns a line when it's a PROBLEM; "Pre-approved $325,000"
  // beside a $325,000 price was just saying the number twice.
  const detailLines = (l) => {
    const source =
      l.source && typeof l.source === "object"
        ? l.source.name || ""
        : leadSourceLabel(l.source, leadsource);
    const approval = l.preApproval === "no" ? "Needs pre-approval" : "";
    return [source, approval];
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <PageHeader
        title="Buyers Pipeline"
        actions={
          <>
            {isPrivileged && (
              <select value={viewAgent} onChange={(e) => setViewAgent(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700">
                <option value="all">👁 Viewing: All Agents</option>
                {agentOptions.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {([emp.firstname, emp.lastname].filter(Boolean).join(" ") || emp.email) +
                      (emp.id === user?.id ? " (Me)" : "")}
                  </option>
                ))}
              </select>
            )}
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showLost} onChange={(e) => setShowLost(e.target.checked)} />
              Show Lost
            </label>
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showWon} onChange={(e) => setShowWon(e.target.checked)} />
              Show Won
            </label>
            <button onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 bg-[#1E293B] hover:opacity-90 text-white rounded-full px-4 py-2 text-sm font-semibold">
              <Plus size={16} /> Add Buyer Lead
            </button>
          </>
        }
      />

      {/* Search — its own row beneath the header (buyer pipeline only). */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative w-full max-w-md">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search buyers — name, area, price…"
            className="w-full text-sm border border-slate-200 rounded-lg pl-8 pr-7 py-1.5 bg-white text-slate-700 placeholder:text-slate-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        <PipelineViewSwitch
          leads={visibleLeads}
          stages={BUYER_STAGE_ORDER}
          parkingStages={BUYER_PARKING_STAGES}
          stageDot={(stage) => (
            <span
              className={`${getBuyerStageColor(stage)} rounded-full size-2.5 inline-block flex-shrink-0`}
            />
          )}
          stageColor={getBuyerStageColor}
          wonLabel="Won · Fully Converted"
          apptLabel="Appt"
          renderSecondary={secondaryLine}
          renderDetails={detailLines}
          canManage={canManage}
          deletedRows={visibleDeleted}
          deletedCount={visibleDeleted.length}
          onRestoreDeleted={restoreDeleted}
          onPurgeDeleted={purgeDeleted}
          onOpen={(lead) => setSelectedId(lead.id)}
          onStageChange={moveStage}
          onDismiss={async (lead) => {
            // Snooze = schedule a touch a week out.
            const res = await updateBuyerLead(lead.id, { nextFollowUp: isoDaysFromNow(7) });
            if (res.success) toast.success("Snoozed a week");
            else toast.error("Couldn't dismiss. Try again.");
          }}
          onMarkLost={async (lead) => {
            // Same flow as the drawer's ⋮ menu.
            const reason = await prompt({
              title: "Mark as Lost",
              label: "Reason this lead is lost or inactive (optional)",
              placeholder: "Went with another agent",
              confirmLabel: "Mark as Lost",
            });
            if (reason === null) return; // cancelled — leave the lead alone
            const res = await updateBuyerLead(lead.id, {
              status: "lost",
              lostReason: reason,
              lostAt: Date.now(),
            });
            if (res.success) toast.success("Marked as Lost");
            else toast.error("Failed");
          }}
          onDelete={async (lead) => {
            const name = clientNames(lead) || "this lead";
            const ok = await confirm({
              title: "Delete lead",
              body: `Delete ${name} from the pipeline?`,
              note: "This removes them from the board entirely.",
              tone: "danger",
              confirmLabel: "Delete",
            });
            if (!ok) return;
            const res = await deleteBuyerLead(lead.id);
            if (res.success) toast.success("Lead deleted");
            else toast.error("Couldn't delete. Try again.");
          }}
        />
      </div>

      {addOpen && <NewBuyerLeadModal onClose={() => setAddOpen(false)} />}
      {selectedLead && (
        <BuyerLeadDrawer
          lead={selectedLead}
          canManage={canManage(selectedLead)}
          onClose={() => setSelectedId(null)}
        />
      )}
      {dialog}
    </div>
  );
}
